import { Nullable } from "sonobello.utilities.react";

import { IExperimentConfiguration } from "../../Experiment/types/ExperimentConfiguration";
import ExperimentKey from "../../Experiment/types/ExperimentKey";
import IExperimentContextProps from "../../Experiment/types/IExperimentContextProps";
import { IFlow } from "./IFlow";
import IFlowResolver, { IFlowAssignment } from "./IFlowResolver";

/**
 * An {@link IFlowResolver} that always resolves to a variant flow regardless of experiment assignment.
 * Warns when the experiment context would have assigned the control flow instead.
 *
 * @remarks
 * This resolver evaluates the experiment assignment for logging/reporting purposes but ignores
 * the result and always returns the variant flow. It's used in forced-variant mode to override
 * experiment-driven flow selection and ensure all users see the experimental variant.
 */
class ExperimentVariantFlowResolver implements IFlowResolver {
  private readonly _variantFlow: IFlow;
  private readonly _experimentKey: ExperimentKey;

  /**
   * @param variantFlow - The flow to always resolve to (returned regardless of experiment state).
   * @param experimentKey - The key of the experiment to evaluate for assignment reporting.
   */
  constructor(variantFlow: IFlow, experimentKey: ExperimentKey) {
    this._variantFlow = variantFlow;
    this._experimentKey = experimentKey;
  }

  readonly invoke = (experimentContext: IExperimentContextProps, sessionFlowName: string): IFlowAssignment => {
    if (this._variantFlow.matchesFlowName(sessionFlowName)) {
      const isVariantNameExactMatch = sessionFlowName === this._variantFlow.name;
      let experimentConfig: Nullable<IExperimentConfiguration> = null;
      try {
        experimentConfig = experimentContext.getExperimentConfiguration(this._experimentKey);
        // Warn if control would have been assigned (exact match on variant name means no control assignment)
        const wouldResolveToControl = !experimentConfig.isActive && !isVariantNameExactMatch;
        if (wouldResolveToControl) {
          console.warn(
            `User was assigned control flow, but ExperimentVariantFlowResolver forced variant flow '${this._variantFlow.name}' for experiment '${this._experimentKey}'.`
          );
        }

        return {
          flow: this._variantFlow,
          experimentConfiguration: isVariantNameExactMatch
            ? { experimentId: experimentConfig.experimentId, isActive: true }
            : experimentConfig
        };
      } catch (e) {
        console.error(e, e);
        console.warn(`Failed to retrieve feature flags for experiment ${this._experimentKey}.`);
        throw new Error(ExperimentVariantFlowResolver.Messages.assignmentDecisionThrew);
      }
    }
    throw new Error(ExperimentVariantFlowResolver.Messages.flowNotResolved);
  };

  static readonly Messages = {
    flowNotResolved: "Flow could not be resolved.",
    assignmentDecisionThrew: "An error occurred while attempting to determine assignment status of the variant flow."
  };
}

export default ExperimentVariantFlowResolver;
